import { gql, useQuery } from '@apollo/client';

// Felder, die Arbeitsliste und Akte gemeinsam nutzen
export const DEBT_CUSTOMER_FIELDS = gql`
  fragment DebtCustomerFields on DebtCustomer {
    stripeCustomerId
    name
    email
    companyId
    companyName
    customerSince
    openAmount
    invoiceCount
    oldestDueDate
    firstOpenAt
    dunningStage
    subscriptionStatuses
    paidInvoiceCount
    paidTotal
    lastPaidAt
    lastLogin
    disputes {
      reason
      status
    }
    signals {
      key
      level
      label
    }
    recommendation
    invoices {
      id
      number
      status
      amountDue
      amountRemaining
      currency
      createdAt
      dueDate
      periodStart
      markedUncollectibleAt
      attemptCount
      failureCode
      failureLabel
      hostedInvoiceUrl
      invoicePdf
      description
      handoverStatus
      handoverError
    }
    debtor {
      businessType
      businessName
      contactName
      email
      vatId
      addressLine1
      addressLine2
      postalCode
      town
      country
      missing
    }
    workflowState
    caseStatus
    caseNote
    caseDecidedAt
    caseDecidedBy
    holdUntil
    caseError
    debtistReference
    debtistStatus
    debtistTotalPaid
    debtistTotalPending
    debtistClosure
    debtistSyncedAt
    feedbackRequired
  }
`;

const DEBT_OVERVIEW = gql`
  ${DEBT_CUSTOMER_FIELDS}
  query DebtOverview($refresh: Boolean) {
    debtOverview(refresh: $refresh) {
      generatedAt
      handoverEnabled
      handoverTarget
      handoverBlockedReason
      customers {
        ...DebtCustomerFields
      }
    }
  }
`;

export type DebtSignalLevel = 'red' | 'yellow' | 'green' | 'block' | 'wait' | 'info';
export type DebtRecommendation = 'WAIT' | 'HOLD' | 'CLARIFY' | 'MISSING_DATA' | 'HANDOVER';
export type DunningStage = 'STRIPE_RETRY' | 'S15' | 'DONE';
export type WorkflowState =
  | 'DUNNING'
  | 'DECIDE'
  | 'FOLLOW_UP'
  | 'IN_PROGRESS'
  | 'AT_DEBTIST'
  | 'PARKED'
  | 'WRITTEN_OFF'
  | 'CLOSED';

export interface DebtSignal {
  key: string;
  level: DebtSignalLevel;
  label: string;
}

export interface DebtInvoice {
  id: string;
  number: string | null;
  status: 'open' | 'uncollectible';
  amountDue: number;
  amountRemaining: number;
  currency: string;
  createdAt: string;
  dueDate: string | null;
  periodStart: string | null;
  markedUncollectibleAt: string | null;
  attemptCount: number;
  failureCode: string | null;
  failureLabel: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  description: string | null;
  handoverStatus: 'HANDED_OVER' | 'FAILED' | null;
  handoverError: string | null;
}

export interface DebtorPreview {
  businessType: string;
  businessName: string | null;
  contactName: string | null;
  email: string | null;
  vatId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  town: string | null;
  country: string | null;
  missing: string[];
}

export interface DebtCustomer {
  stripeCustomerId: string;
  name: string;
  email: string | null;
  companyId: string | null;
  companyName: string | null;
  customerSince: string | null;
  openAmount: number;
  invoiceCount: number;
  oldestDueDate: string | null;
  firstOpenAt: string;
  dunningStage: DunningStage;
  subscriptionStatuses: string[];
  paidInvoiceCount: number;
  paidTotal: number;
  lastPaidAt: string | null;
  lastLogin: string | null;
  disputes: Array<{ reason: string; status: string }>;
  signals: DebtSignal[];
  recommendation: DebtRecommendation;
  invoices: DebtInvoice[];
  debtor: DebtorPreview;
  workflowState: WorkflowState;
  caseStatus: string | null;
  caseNote: string | null;
  caseDecidedAt: string | null;
  caseDecidedBy: string | null;
  holdUntil: string | null;
  caseError: string | null;
  debtistReference: string | null;
  debtistStatus: string | null;
  debtistTotalPaid: number | null;
  debtistTotalPending: number | null;
  debtistClosure: string | null;
  debtistSyncedAt: string | null;
  feedbackRequired: boolean;
}

export interface DebtOverviewData {
  debtOverview: {
    generatedAt: string;
    handoverEnabled: boolean;
    handoverTarget: 'sandbox' | 'production' | null;
    handoverBlockedReason: string | null;
    customers: DebtCustomer[];
  };
}

export function useDebtOverviewQuery() {
  // Der Server cacht 10 Minuten; „Aktualisieren“ ruft refetch({ refresh: true })
  return useQuery<DebtOverviewData, { refresh?: boolean }>(DEBT_OVERVIEW, {
    variables: { refresh: false },
    notifyOnNetworkStatusChange: true,
  });
}
